/**
 * Patología Form Dialog Component
 *
 * Formulario para crear o editar patologías del paciente.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

import {
  useCreatePatologia,
  useUpdatePatologia,
  type PatologiaListItem,
} from "@/hooks/use-patologias";
import { useApiError } from "@/hooks/use-api-error";

// Form schema
const patologiaSchema = z.object({
  nombre: z.string().min(1, "patologias.validation.nameRequired").max(200),
  fecha_registro: z.string().min(1, "patologias.validation.dateRequired"),
  activa: z.boolean(),
  notas: z.string().max(1000).optional(),
});

type FormData = z.infer<typeof patologiaSchema>;

interface PatologiaFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pacienteId: number;
  pacienteNombre: string;
  patologia?: PatologiaListItem | null;
  onSuccess?: () => void;
}

export function PatologiaFormDialog({
  open,
  onOpenChange,
  pacienteId,
  pacienteNombre,
  patologia,
  onSuccess,
}: PatologiaFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [error, setError] = useState<string | null>(null);
  const isEditing = !!patologia;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(patologiaSchema),
    defaultValues: {
      nombre: "",
      fecha_registro: format(new Date(), "yyyy-MM-dd"),
      activa: true,
      notas: "",
    },
  });

  const activa = watch("activa");

  // Reset form when dialog opens/closes or patologia changes
  useEffect(() => {
    if (open) {
      setError(null);
      if (patologia) {
        reset({
          nombre: patologia.nombre,
          fecha_registro: patologia.fecha_registro.split("T")[0],
          activa: patologia.activa,
          notas: patologia.notas || "",
        });
      } else {
        reset({
          nombre: "",
          fecha_registro: format(new Date(), "yyyy-MM-dd"),
          activa: true,
          notas: "",
        });
      }
    }
  }, [open, patologia, reset]);

  const createMutation = useCreatePatologia({
    onSuccess: () => {
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  const updateMutation = useUpdatePatologia({
    onSuccess: () => {
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  const onSubmit = (data: FormData) => {
    setError(null);

    if (isEditing && patologia) {
      updateMutation.mutate({
        id: patologia.id,
        data: {
          nombre: data.nombre,
          fecha_registro: data.fecha_registro,
          activa: data.activa,
          notas: data.notas || "",
        },
      });
    } else {
      createMutation.mutate({
        paciente: pacienteId,
        nombre: data.nombre,
        fecha_registro: data.fecha_registro,
        activa: data.activa,
        notas: data.notas || "",
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t("patologias.editTitle", "Editar Patología")
              : t("patologias.createTitle", "Nueva Patología")}
          </DialogTitle>
          <DialogDescription>
            {t("patologias.formDescription", "Paciente: {{name}}", { name: pacienteNombre })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <div className="grid gap-4">
            {/* Nombre de la Patología */}
            <div className="space-y-2">
              <Label htmlFor="nombre">
                {t("patologias.fields.name", "Nombre de la Patología")}
              </Label>
              <Input
                id="nombre"
                placeholder={
                  isEditing ? undefined : t("patologias.namePlaceholder", "Ej: Diabetes Tipo 2")
                }
                className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                {...register("nombre")}
              />
              {errors.nombre && (
                <p className="text-sm text-destructive">{t(errors.nombre.message as string)}</p>
              )}
            </div>

            {/* Fecha de Registro */}
            <div className="space-y-2">
              <Label htmlFor="fecha_registro">
                {t("patologias.fields.date", "Fecha de Registro")}
              </Label>
              <Input id="fecha_registro" type="date" {...register("fecha_registro")} />
              <p className="text-xs text-muted-foreground">
                {t("patologias.dateDescription", "Fecha en que se diagnosticó o registró")}
              </p>
              {errors.fecha_registro && (
                <p className="text-sm text-destructive">
                  {t(errors.fecha_registro.message as string)}
                </p>
              )}
            </div>

            {/* Condición Activa */}
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="activa">{t("patologias.fields.active", "Condición Activa")}</Label>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "patologias.activeDescription",
                    "¿El paciente actualmente padece esta condición?"
                  )}
                </p>
              </div>
              <Switch
                id="activa"
                checked={activa}
                onCheckedChange={(checked) => setValue("activa", checked)}
              />
            </div>

            {/* Notas */}
            <div className="space-y-2">
              <Label htmlFor="notas">{t("patologias.fields.notes", "Notas")}</Label>
              <Textarea
                id="notas"
                placeholder={
                  isEditing
                    ? undefined
                    : t(
                        "patologias.notesPlaceholder",
                        "Observaciones adicionales sobre la patología..."
                      )
                }
                className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                rows={3}
                {...register("notas")}
              />
              {errors.notas && (
                <p className="text-sm text-destructive">{t(errors.notas.message as string)}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
